import {
  htmlIframeElementModels,
  htmlIframeRenderers,
} from '@components/HtmlIframeRenderer'
import { MediaRender } from '@components/MediaRender'
import { Slider } from '@components/Slider'
import { ILesson, IMedia } from '@dtos/Course'
import { useDynamicSnapPoints } from '@hooks/useDynamicSnapPoints'
import { useEnrollment } from '@hooks/useEnrollment'
import { useThemeColor } from '@hooks/useThemeColor'
import { GestureReponderEvent, isWeb, useMedia } from '@tamagui/core'
import {
  ILessonExtraContentAvailability,
  lessonExtraContentAvailability,
} from '@utils/lessonExtraContentAvailability'
import { openWebBrowser } from '@utils/openWebBrowser'
import { useLocalSearchParams } from 'expo-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useWindowDimensions } from 'react-native'
import RenderHtml from 'react-native-render-html'
import { Text, View, XStack, YStack } from 'tamagui'
import { ExtraContentCard } from '../extra_contents/ExtraContentCard'
import { ExtraContentModal } from '../extra_contents/ExtraContentModal'
import { NotSupported } from '../extra_contents/NotSupported'

import { CommentThread } from '@components/Comments'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { HeroTabs } from '@components/HeroTabs'
import { useCurrentMedia } from '@hooks/useCurrentMedia'
import { useForumTopicsCount } from '@services/api/graphql/comments'
import { useGetMedia } from '@services/api/graphql/getMedia'
import { useLessonTabStore } from '@stores/useLessonTabStore'

interface IDisableBtns {
  isFirstContent: boolean
  isLastContent: boolean
}

interface IModalExtraMediaProps extends IMedia {
  index?: number | null
  isMain?: boolean
  disableBtns?: IDisableBtns
}

interface ITabsContentContainerProps {
  currentLesson: ILesson | null
  withoutTabContent?: boolean
  withoutTabs?: boolean
  currentTab?: string
  onTabChange?: (tab: string) => void
  description: string
  extraMedias: IMedia[]
}

export function TabsContentContainer({
  currentLesson,
  currentTab,
  onTabChange,
  withoutTabContent,
  withoutTabs,
  description,
  extraMedias,
}: ITabsContentContainerProps) {
  const { width, height } = useWindowDimensions()
  const media = useMedia()
  const [openExtraContentModal, setOpenExtraContentModal] = useState(false)
  const [extraMedia, setExtraMedia] = useState<IModalExtraMediaProps | null>(
    null,
  )
  const { setCurrentMedia } = useCurrentMedia()
  const { enrollment } = useEnrollment()

  const { courseId, enrollmentId } = useLocalSearchParams()
  const {
    data: mediaData,
    loading: isLoadingMedia,
    refetch,
  } = useGetMedia({
    courseId: courseId?.toString() ?? '0',
    enrollmentId: enrollmentId?.toString() ?? '0',
    id: extraMedia?.id?.toString() ?? '0',
    enabled: openExtraContentModal && !!extraMedia?.id,
  })

  const paddingBottom = useMemo(() => {
    if (!isWeb) return height * 0.2
    return 0
  }, [height])

  const { setTabs } = useLessonTabStore()

  const cardWidth = useMemo(() => {
    if (media.gtMd) return 324
    if (media.gtSm) return 407
    return 242
  }, [media.gtMd, media.gtSm])

  const slidesSpacing = 24
  const containerPadding = useMemo(() => {
    return media.gtMd ? 40 : 16
  }, [media.gtMd])

  const perView = useMemo(() => {
    const totalWidth = width - containerPadding
    const cardWithSpacing = cardWidth + slidesSpacing
    return totalWidth / cardWithSpacing
  }, [width, containerPadding, cardWidth])

  const availabilityById = useMemo(() => {
    const byId: Record<string, ILessonExtraContentAvailability> = {}
    if (!currentLesson) return byId

    for (const content of extraMedias ?? []) {
      byId[content.id] = lessonExtraContentAvailability(
        currentLesson,
        content.id,
        enrollment,
      )
    }
    return byId
  }, [currentLesson, extraMedias, enrollment])

  const isExtraMediaAvailable = useCallback(
    (id: number) => availabilityById[id]?.isAvailable ?? true,
    [availabilityById],
  )

  const totalSlides = useMemo(() => {
    return extraMedias?.length ?? 0
  }, [extraMedias?.length])

  const hasExtraContent = Boolean(
    currentLesson && currentLesson?.extraMedias?.length > 0,
  )
  const hasDescription = Boolean(description)
  const isPDF = extraMedia?.fileExt?.toLowerCase() === 'pdf'
  const mediaType = isPDF ? 'pdf' : extraMedia?.type || 'Text'

  const handlePressDownload = useCallback(
    (e: GestureReponderEvent, link: string | null | undefined) => {
      if (link) {
        openWebBrowser(link)
      }
      e.stopPropagation()
    },
    [],
  )

  const setExtraContentModal = useCallback((media: IModalExtraMediaProps) => {
    setExtraMedia(media)
    setOpenExtraContentModal(true)
  }, [])

  useEffect(() => {
    if (extraMedia) {
      setCurrentMedia({
        media: extraMedia,
        enrollmentId: Number(enrollmentId),
        courseId: Number(courseId),
      })
    }
  }, [extraMedia, enrollmentId, courseId, setCurrentMedia])

  const changePreviewContent = useCallback(
    (i: number) => {
      if (!extraMedias) return
      if (extraMedia?.index === undefined || extraMedia?.index === null) return
      if (!extraMedia?.isMain) return

      let nextIndex = extraMedia.index + i
      while (
        nextIndex >= 0 &&
        nextIndex < extraMedias.length &&
        !isExtraMediaAvailable(extraMedias[nextIndex].id)
      ) {
        nextIndex += i
      }
      if (nextIndex < 0 || nextIndex >= extraMedias.length) return

      setExtraMedia({
        ...extraMedias[nextIndex],
        index: nextIndex,
        isMain: true,
        disableBtns: {
          isFirstContent: nextIndex === 0,
          isLastContent: nextIndex === extraMedias.length - 1,
        },
      })
      refetch()
    },
    [extraMedias, extraMedia, refetch, isExtraMediaAvailable],
  )

  const textColor = useThemeColor('text-primary')

  const descriptionContent = useMemo(() => {
    return (
      <View
        h="100%"
        paddingHorizontal="$spacing-inset-xs"
        paddingVertical="$spacing-inset-md"
        paddingBottom={paddingBottom}
      >
        {description ? (
          <RenderHtml
            contentWidth={width}
            source={{ html: description }}
            // A descrição também aceita HTML colado pelo produtor; sem estes
            // props o RenderHtml apaga <iframe> (ver HtmlIframeRenderer).
            customHTMLElementModels={htmlIframeElementModels}
            renderers={htmlIframeRenderers}
            baseStyle={{
              color: textColor,
              fontSize: 12,
              fontFamily: 'Mulish-Regular',
            }}
          />
        ) : (
          <Text
            color="$text-primary"
            fontFamily="$mulishRegular"
            fontSize="$font-size-sm"
          >
            Sem descrição disponível
          </Text>
        )}
      </View>
    )
  }, [description, textColor, width, paddingBottom])

  const complementaryContent = useMemo(() => {
    const handlePressOpenModal = (
      media: IMedia,
      index: number,
      isMain: boolean,
      nodes: number,
    ) => {
      const mediaToOpen = {
        ...media,
        id: media.id,
        type: media.type || null,
        fileUrl: mediaData?.media?.fileUrl || null,
        fileExt: media.fileExt || null,
        text: media.text || null,
        title: media.title,
        index,
        isMain,
        disableBtns: {
          isFirstContent: index === 0,
          isLastContent: index === nodes - 1,
        },
      }

      setExtraContentModal(mediaToOpen)
    }

    return (
      <YStack
        paddingLeft="$spacing-inset-xs"
        overflow="hidden"
        paddingBottom={paddingBottom}
      >
        <Slider
          slidesTotalNumber={totalSlides}
          slidesPerView={perView}
          displaySliderControls={totalSlides > perView}
          slidesSpacing={16}
          paddingRight="$spacing-inset-xs"
          sliderTitle={
            <XStack
              backgroundColor="$info-300"
              paddingHorizontal="$spacing-inset-xxs"
              paddingVertical="$spacing-inset-quarck"
              borderRadius="$border-radius-pill"
              marginVertical="$spacing-inset-sm"
            >
              <Text
                color="$text-on-accent"
                fontFamily="$mulishSemiBold"
                fontWeight="600"
                fontSize={14}
              >
                {totalSlides} conteúdos
              </Text>
            </XStack>
          }
        >
          {[...(extraMedias || [])].reverse().map((content, index) => (
            <ExtraContentCard
              key={content.id}
              extraContent={{
                id: content.id,
                title: content.title,
                media: {
                  id: content.id,
                  title: content.title,
                  type: content.type,
                  duration: null,
                  fileUrl: mediaData?.media?.fileUrl,
                  fileExt: content.fileExt,
                  text: content.text,
                  hostKey: content.hostKey,
                  host: content.host,
                  organizationUuid: content.organizationUuid,
                  originUrl: content.originUrl,
                },
                downloadable: !!content.fileUrl || !!content.fileExt,
                releaseAt: null,
                releaseAfter: null,
                courseClassReleaseOffset: null,
                createdAt: '',
              }}
              isLessonAvailable
              availability={availabilityById[content.id]}
              width={cardWidth}
              minHeight={210}
              handlePressDownload={handlePressDownload}
              handlePressOpenModal={() =>
                handlePressOpenModal(
                  {
                    id: content.id,
                    title: content.title,
                    type: content.type,
                    duration: null,
                    fileUrl: mediaData?.media?.fileUrl,
                    fileExt: content.fileExt,
                    text: content.text,
                    hostKey: content.hostKey,
                    host: content.host,
                    organizationUuid: content.organizationUuid,
                    originUrl: content.originUrl,
                  },
                  index,
                  true,
                  extraMedias?.length || 0,
                )
              }
            />
          ))}
        </Slider>
      </YStack>
    )
  }, [
    paddingBottom,
    totalSlides,
    perView,
    extraMedias,
    mediaData?.media?.fileUrl,
    availabilityById,
    cardWidth,
    handlePressDownload,
    setExtraContentModal,
  ])

  const courseContentId = currentLesson?.courseContentId
  const { hasTopics, commentsEnabled: forumCommentsEnabled } =
    useForumTopicsCount({
      courseContentId: courseContentId ? String(courseContentId) : null,
    })
  // O legado esconde a aba de comentários do colaborador; aqui é o mesmo
  // critério, e evita oferecer um formulário que o servidor recusaria.
  const { isPreview } = useAdminPreview()
  const commentsEnabled = forumCommentsEnabled && !isPreview

  const commentsContent = useMemo(() => {
    if (!courseContentId) return null
    return (
      <View paddingBottom={paddingBottom}>
        <CommentThread courseContentId={String(courseContentId)} />
      </View>
    )
  }, [courseContentId, paddingBottom])

  const tabs = useMemo(
    () => [
      {
        label: 'Descrição',
        value: 'description',
        content: descriptionContent,
        defaultActive: hasDescription,
        isDisabled: !hasDescription,
      },
      {
        label: 'Conteúdo Complementar',
        value: 'complementary',
        content: complementaryContent,
        defaultActive: !hasDescription && hasExtraContent,
        isDisabled: !hasExtraContent,
      },
      ...(commentsEnabled
        ? [
            {
              label: 'Comentários',
              value: 'comments',
              content: commentsContent,
              defaultActive: !hasDescription && !hasExtraContent && hasTopics,
              isDisabled: false,
            },
          ]
        : []),
    ],
    [
      hasDescription,
      hasExtraContent,
      hasTopics,
      commentsEnabled,
      descriptionContent,
      complementaryContent,
      commentsContent,
    ],
  )

  useEffect(() => {
    setTabs(tabs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasDescription,
    hasExtraContent,
    hasTopics,
    commentsEnabled,
    currentLesson?.id,
    setTabs,
  ])

  return (
    <>
      <HeroTabs
        key={currentLesson?.id?.toString() || ''}
        orientation="horizontal"
        tabs={tabs}
        withBorderTop={true}
        borderBottomRadius={
          media.gtSm ? '$border-radius-xlg' : '$border-radius-none'
        }
        onTabChange={onTabChange}
        currentTab={currentTab}
        withoutTabs={withoutTabs}
        withoutTabContent={withoutTabContent}
      />

      <ExtraContentModal
        mediaType={mediaType}
        open={openExtraContentModal}
        onClose={() => setOpenExtraContentModal(false)}
        title={extraMedia?.title || ''}
        onChangeContent={(i: number) => changePreviewContent(i)}
        disabledBtns={extraMedia?.disableBtns}
        snapPoints={useDynamicSnapPoints().snapPoints}
        shouldAdapt={false}
        zIndex={100000}
      >
        {extraMedia && (
          <MediaRender
            media={{
              ...extraMedia,
              fileUrl: mediaData?.media?.fileUrl,
            }}
            location="extraContent"
            isLoading={isLoadingMedia}
            fileUrl={mediaData?.media?.fileUrl}
            closeModal={() => setOpenExtraContentModal(false)}
          >
            <NotSupported
              media={{
                ...extraMedia,
                fileUrl: mediaData?.media?.fileUrl,
              }}
              fileUrl={mediaData?.media?.fileUrl}
            />
          </MediaRender>
        )}
      </ExtraContentModal>
    </>
  )
}
